import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, CheckCircle2 } from "lucide-react";

const KILKENNY_IMG = "https://images.pexels.com/photos/36704541/pexels-photo-36704541.jpeg";
const MAP_EMBED = "https://www.google.com/maps?q=8%20Ormonde%20Street%2C%20Kilkenny%2C%20Ireland&output=embed";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] },
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const api = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await axios.post(`${api}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      setStatus("sent");
      toast.success(res.data.message || "Enquiry received. We will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
      if (typeof window.gtag === "function") {
        window.gtag("event", "contact_form_submit");
      }
    } catch (err) {
      setStatus("error");
      toast.error("Something went wrong sending your enquiry. Please try again or call us directly.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <motion.div {...fadeUp}>
            <p className="overline-label mb-6 flex items-center gap-4" data-testid="contact-overline">
              <span className="inline-block h-px w-10 bg-gold" />
              Get in Touch
            </p>
            <h2 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-navy sm:text-5xl" data-testid="contact-heading">
              Begin the<br /><em className="italic text-gold">conversation.</em>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted">
              Whether it is a first set of accounts or a full audit, the first step
              is a conversation. Write to us, call, or call in — the kettle is on.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="mt-12 space-y-0 border-t hairline-gold" data-testid="contact-office-info">
            <div className="flex items-start gap-5 border-b hairline-gold py-6">
              <MapPin size={20} className="mt-1 shrink-0 text-gold" />
              <div>
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/50">Office</p>
                <p className="mt-1.5 font-serif text-xl text-navy">8 Ormonde Street, Kilkenny, Ireland</p>
              </div>
            </div>
            <div className="flex items-start gap-5 border-b hairline-gold py-6">
              <Phone size={20} className="mt-1 shrink-0 text-gold" />
              <div>
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/50">Telephone</p>
                <a href="tel:+353871081212" data-testid="contact-phone-link" className="mt-1.5 block font-serif text-xl text-navy transition-colors hover:text-gold">
                  087 108 1212
                </a>
              </div>
            </div>
            <div className="flex items-start gap-5 border-b hairline-gold py-6">
              <Mail size={20} className="mt-1 shrink-0 text-gold" />
              <div>
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/50">Email</p>
                <a href="mailto:info@abaccountant.ie" data-testid="contact-email-link" className="mt-1.5 block font-serif text-xl text-navy transition-colors hover:text-gold">
                  info@abaccountant.ie
                </a>
              </div>
            </div>
            <div className="flex items-start gap-5 border-b hairline-gold py-6" data-testid="contact-hours">
              <Clock size={20} className="mt-1 shrink-0 text-gold" />
              <div>
                <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/50">Opening Hours</p>
                <p className="mt-1.5 font-serif text-xl text-navy">Monday – Friday · 9:00am – 6:00pm</p>
                <p className="mt-1 text-sm text-muted">Saturday &amp; Sunday · Closed</p>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="relative mt-12">
            <div className="absolute -left-4 -top-4 h-full w-full border hairline-gold" aria-hidden="true" />
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={KILKENNY_IMG}
                alt="Ormonde Street, Kilkenny"
                className="h-full w-full object-cover grayscale transition-all duration-700 hover:grayscale-0"
                data-testid="contact-kilkenny-image"
              />
              <div className="absolute inset-0 bg-navy/10" />
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="mt-8 overflow-hidden border hairline-gold" data-testid="contact-map">
            <iframe
              title="AB & Co Accountant — 8 Ormonde Street, Kilkenny"
              src={MAP_EMBED}
              className="h-64 w-full grayscale-[60%]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <motion.form
            {...fadeUp}
            id="contact-form"
            onSubmit={onSubmit}
            className="border hairline-gold bg-paper/50 p-8 lg:p-12"
            data-testid="contact-form"
          >
            <p className="font-serif text-2xl italic text-navy">Tell us about your business.</p>

            <div className="mt-10 space-y-8">
              <div>
                <label htmlFor="contact-name" className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/60">
                  Full Name <span className="text-gold">*</span>
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  type="text"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className="editorial-input mt-2"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/60">
                  Email Address <span className="text-gold">*</span>
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@company.ie"
                  className="editorial-input mt-2"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/60">
                  Phone <span className="normal-case tracking-normal text-navy/40">(optional)</span>
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-phone-input"
                  type="tel"
                  maxLength={40}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="087 000 0000"
                  className="editorial-input mt-2"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-navy/60">
                  How Can We Help? <span className="text-gold">*</span>
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  maxLength={4000}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="A few lines about your business and what you need…"
                  className="editorial-input mt-2 resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={status === "sending"}
              className="group relative mt-10 w-full overflow-hidden bg-navy px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.2em] text-cream disabled:opacity-60"
            >
              <span className="absolute inset-0 translate-y-full bg-gold transition-transform duration-500 ease-out group-hover:translate-y-0" />
              <span className="relative transition-colors duration-500 group-hover:text-navy">
                {status === "sending" ? "Sending…" : "Send Enquiry"}
              </span>
            </button>

            <p data-testid="contact-form-trust-stats" className="mt-6 text-center font-serif text-base italic text-navy/60">
              Trusted by <span className="not-italic font-semibold text-navy">420+</span> businesses ·{" "}
              <span className="not-italic font-semibold text-navy">750+</span> projects completed ·{" "}
              <span className="not-italic font-semibold text-navy">8</span> years in business
            </p>

            {status === "sent" && (
              <div data-testid="contact-success-message" className="mt-6 flex items-center gap-3 border border-gold/50 bg-gold/10 px-5 py-4">
                <CheckCircle2 size={20} className="shrink-0 text-gold" />
                <p className="text-sm text-navy">
                  Thank you — your enquiry has been received. We will respond within one working day.
                </p>
              </div>
            )}
            {status === "error" && (
              <p data-testid="contact-error-message" className="mt-6 text-sm text-red-700">
                We could not send your enquiry just now. Please call us on 087 108 1212.
              </p>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}
